import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Switch } from '@coursera-web/cds-labs';

import { Grid, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import {
  getShouldPlaySoundEffects,
  setShouldPlaySoundEffects,
} from 'bundles/daily-goals/utils/soundEffectsPreferences';

import _t from 'i18n!nls/account-settings';

type Variant = 'section' | 'field';

type Props = {
  /**
   * `'section'` (default) styles the label as a section heading (`subtitleLarge` /
   * `h2`) for the v2 CDS settings shell where Sound effects gets its own
   * SectionCard alongside the Appearance card. `'field'` styles it as a control
   * label (`subtitleMedium` / `h3`) for the legacy SettingsApp shell where it
   * sits inside the Appearance section above the Color theme dropdown.
   */
  variant?: Variant;
};

export const DEEP_LINK_HASH = '#sound-effects';

const SoundEffectsField = ({ variant = 'section' }: Props) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(getShouldPlaySoundEffects());
  const track = useTracker();
  const containerRef = useRef<HTMLDivElement>(null);

  // Deep link from the sound celebration dialog (#sound-effects): center the card and focus the
  // toggle. The card's highlight is owned by SectionCard via its `highlighted` prop.
  useEffect(() => {
    if (variant !== 'section' || typeof window === 'undefined' || window.location.hash !== DEEP_LINK_HASH) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Defer a frame so sibling mount effects (e.g. the shell removing its loading overlay) settle
    // first, and so the SectionCard highlight effect can still read the hash before we strip it.
    const raf = window.requestAnimationFrame(() => {
      containerRef.current?.querySelector<HTMLElement>('[role="switch"]')?.focus({ preventScroll: true });
      containerRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
      // Strip the hash so a refresh/back-nav doesn't re-trigger.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    });

    return () => window.cancelAnimationFrame(raf);
  }, [variant]);

  const handleToggle = (next: boolean) => {
    setIsEnabled(next);
    setShouldPlaySoundEffects(next);
    track('change_select', {
      select: {
        name: 'sound_effects',
        value: [next ? 'on' : 'off'],
        label: 'Sound effects',
      },
    });
  };

  const labelVariant = variant === 'section' ? 'subtitleLarge' : 'subtitleMedium';
  const labelComponent = variant === 'section' ? 'h2' : 'h3';
  const descriptionVariant = variant === 'section' ? 'bodySecondary' : 'bodyPrimary';

  return (
    <Grid ref={containerRef} container spacing={16} alignItems="flex-start" wrap="nowrap">
      <Grid item xs>
        <Typography2 variant={labelVariant} component={labelComponent}>
          {_t('Sound effects')}
        </Typography2>
        <Typography2 variant={descriptionVariant} component="p" color="supportText">
          {_t('Celebratory sounds that play after you complete items, modules, and pass assessments.')}
        </Typography2>
      </Grid>
      <Grid item>
        <Switch aria-label={_t('Sound effects')} selected={isEnabled} onToggle={handleToggle} />
      </Grid>
    </Grid>
  );
};

export default SoundEffectsField;
